import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from './api';
import omit from '../utils/omit';

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api/embargo`;

export type EmbargoKeyword = {
  _id: string;
  keyword: string;
  displayName: string;
  from: string;
  to: string;
  createdAt: string;
  updatedAt: string;
};

type GetEmbargoKeywordPayload = {
  search?: string;
  active?: boolean;
  page: number;
  limit: number;
};

type GetEmbargoKeywordsResponse = {
  count: number;
  items: EmbargoKeyword[];
};

type CreateEmbargoKeywordPayload = Omit<EmbargoKeyword, '_id' | 'createdAt' | 'updatedAt'>;

type EditEmbargoKeywordPayload = { _id: string } & CreateEmbargoKeywordPayload;

type BulkCreateEmbargoKeywordPayload = {
  keywords: { keyword: string; displayName: string }[];
  from: string;
  to: string;
};

type BulkCreateEmbargoKeywordResponse = {
  created: number;
};

export const apiEmbargoKeyword = createApi({
  reducerPath: 'apiEmbargoKeyword',
  baseQuery: (...args) => baseQueryWithReAuth(...args, { baseUrl: BASE_URL }),
  tagTypes: ['embargoKeywords'],
  endpoints: (build) => ({
    getEmbargoKeywords: build.query<GetEmbargoKeywordsResponse, GetEmbargoKeywordPayload>({
      query: (params) => ({
        url: '/',
        method: 'GET',
        params
      }),
      providesTags: ['embargoKeywords']
    }),
    createEmbargoKeyword: build.mutation<void, CreateEmbargoKeywordPayload>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body
      }),
      invalidatesTags: ['embargoKeywords']
    }),
    editEmbargoKeyword: build.mutation<void, EditEmbargoKeywordPayload>({
      query: (body) => ({
        url: `/${body._id}`,
        method: 'PUT',
        body: omit(body, ['_id'])
      }),
      invalidatesTags: ['embargoKeywords']
    }),
    deleteEmbargoKeyword: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['embargoKeywords']
    }),
    bulkCreateEmbargoKeywords: build.mutation<BulkCreateEmbargoKeywordResponse, BulkCreateEmbargoKeywordPayload>({
      query: (body) => ({
        url: '/bulk',
        method: 'POST',
        body
      }),
      invalidatesTags: ['embargoKeywords']
    })
  })
});

export const {
  useLazyGetEmbargoKeywordsQuery,
  useCreateEmbargoKeywordMutation,
  useEditEmbargoKeywordMutation,
  useDeleteEmbargoKeywordMutation,
  useBulkCreateEmbargoKeywordsMutation
} = apiEmbargoKeyword;
